// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";

// Styles
import { getFeatureRowLabelColor } from "src/styles/mui/muiStylesHelper";

const VisualAcuity = () => {
  const { t } = useTranslation("operating-manual");
  const theme = useTheme();
  const rowLabelColor = getFeatureRowLabelColor(theme);

  const tableContent = [
    {
      title: t("Tumbling E"),
      subtitle: t("Letter E in 4 orientations"),
      content: t(
        "Patients indicate the direction the letter E points (up, down, left, or right)"
      ),
    },
    {
      title: t("Bailey-Lovie Letters"),
      subtitle: t("Letters"),
      content: t(
        "LogMAR scale; 5 letters per row with uniform spacing between letters and rows."
      ),
    },
    {
      title: t("Landolt C"),
      subtitle: t("Broken Ring"),
      content: t(
        "Patients identify the direction of the gap in a 'C' shape, commonly used in research and international testing"
      ),
    },
    {
      title: t("Snellen"),
      subtitle: t("Letters"),
      content: t(
        "The classic chart with rows of letters that decrease in size"
      ),
    },
    {
      title: t("ETDRS"),
      subtitle: t("Sloan Letters"),
      content: t(
        "LogMAR scale; 5 letters per row with uniform spacing between letters and rows."
      ),
    },
  ];

  const getTableRow = useCallback(
    (title: string, subtitle: string, content: string) => (
      <Grid container key={title}>
        <Grid
          item
          xs={4}
          sm={2}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography color={rowLabelColor} fontSize="14px" fontWeight={600}>
            {title}
          </Typography>
        </Grid>
        <Grid
          item
          xs={4}
          sm={2}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography fontSize="14px" fontWeight={600}>
            {subtitle}
          </Typography>
        </Grid>
        <Grid
          item
          xs={4}
          sm={8}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography fontSize="14px">{content}</Typography>
        </Grid>
      </Grid>
    ),
    [rowLabelColor]
  );

  return (
    <DefaultCard>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Grid container>
          <Grid
            item
            xs={4}
            sm={2}
            sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Typography fontWeight={600}>{t("Test Pattern")}</Typography>
          </Grid>
          <Grid
            item
            xs={4}
            sm={2}
            sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Typography fontWeight={600}>{t("Classification")}</Typography>
          </Grid>
          <Grid
            item
            xs={4}
            sm={8}
            sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Typography fontWeight={600}>{t("Description")}</Typography>
          </Grid>
        </Grid>

        {tableContent.map((tableItem) =>
          getTableRow(tableItem.title, tableItem.subtitle, tableItem.content)
        )}
      </Box>
    </DefaultCard>
  );
};

export default VisualAcuity;
